import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";

const RestaurantContext = createContext(null);

/**
 * RestaurantContext Provider
 * Manages the current restaurant slug from the URL and locationId from query params
 * Provides it to all child components
 */
export const RestaurantProvider = ({ children }) => {
    const params = useParams();
    const location = useLocation();
    /** Slug must be synchronous on first paint (e.g. checkout refresh); useParams is available immediately */
    const restaurantSlug = params.restaurantSlug || null;
    const [locationId, setLocationId] = useState(null);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const locId = searchParams.get("locationId");
        if (locId) {
            const parsedLocationId = parseInt(locId, 10);
            setLocationId(Number.isNaN(parsedLocationId) ? null : parsedLocationId);
        } else {
            setLocationId(null);
        }
    }, [location.search]);

    const value = useMemo(
        () => ({
            restaurantSlug,
            locationId,
            isRestaurantSelected: !!restaurantSlug,
        }),
        [restaurantSlug, locationId]
    );

    return <RestaurantContext.Provider value={value}>{children}</RestaurantContext.Provider>;
};

/**
 * Hook to access restaurant context
 * @returns {Object} { restaurantSlug, locationId, isRestaurantSelected }
 */
export const useRestaurant = () => {
    const context = useContext(RestaurantContext);
    if (!context) {
        throw new Error("useRestaurant must be used within a RestaurantProvider");
    }
    return context;
};

export default RestaurantContext;
